import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const LAST_UPDATED = '13 September 2026';

const External = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
);

const PrivacyPolicy = () => {
  // Usually opened from the footer — start at the top, not the old scroll position
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  return (
    <div className="policy-page">
      <div className="container">
        <article className="policy">
          <header className="policy-head">
            <span className="eyebrow">Legal</span>
            <h1 className="page-title">Privacy Policy</h1>
            <p className="policy-updated">Last updated: {LAST_UPDATED}</p>
          </header>

          <p className="policy-lead">
            DJI Store Nepal (&ldquo;we&rdquo;, &ldquo;us&rdquo;) runs djistorenepal.com. This policy explains
            what information we collect when you shop, sell second-hand gear or read our blog, how we use it,
            and the choices you have.
          </p>

          <section>
            <h2>1. Information we collect</h2>
            <h3>Information you give us</h3>
            <ul>
              <li>
                <strong>Account details</strong> — your name, username, email address, phone number and
                password. Passwords are stored in encrypted (hashed) form, so we can never see them.
              </li>
              <li>
                <strong>Order details</strong> — your delivery address, phone number, order notes and the
                products you buy.
              </li>
              <li>
                <strong>Payment records</strong> — Khalti and Fonepay payments happen on their own secure pages.
                We never receive your wallet PIN, bank login or card number; we only keep the amount, status
                and transaction reference they send back to us.
              </li>
              <li>
                <strong>Second-hand listings</strong> — item details, photos, location and a contact phone
                number. Once a listing is approved, its details, photos and your display name are public. Your
                phone number and email are only visible to you and our team.
              </li>
              <li>
                <strong>Messages</strong> — anything you send us by email, phone or WhatsApp.
              </li>
            </ul>
            <h3>Information collected automatically</h3>
            <p>
              Like most websites, our servers record basic technical information such as your IP address,
              browser type, device and the pages you visit. Our advertising partners also collect information
              through cookies — see section 3.
            </p>
          </section>

          <section>
            <h2>2. How we use your information</h2>
            <ul>
              <li>To process, deliver and follow up on your orders.</li>
              <li>To confirm your payments with Khalti and Fonepay.</li>
              <li>To create and manage your account and keep you signed in.</li>
              <li>To review second-hand listings before they go live.</li>
              <li>To answer your questions and provide warranty and after-sales support.</li>
              <li>To prevent fraud and keep the website secure.</li>
              <li>To understand how the website is used so we can improve it.</li>
            </ul>
            <p><strong>We do not sell your personal information.</strong></p>
          </section>

          <section>
            <h2>3. Advertising and cookies</h2>
            <p>We show ads on this website through Google AdSense. This means:</p>
            <ul>
              <li>
                Third-party vendors, including Google, use cookies to serve ads based on your previous visits to
                this website and other websites.
              </li>
              <li>
                Google&rsquo;s use of advertising cookies lets Google and its partners show you ads based on your
                visits to this website and/or other websites on the internet.
              </li>
              <li>
                You can opt out of personalised advertising in
                {' '}<External href="https://adssettings.google.com">Google Ads Settings</External>. You can also
                opt out of some other vendors&rsquo; cookies for personalised ads at
                {' '}<External href="https://www.aboutads.info/choices">www.aboutads.info/choices</External>.
              </li>
            </ul>
            <p>
              To learn more, read
              {' '}<External href="https://policies.google.com/technologies/partner-sites">
                how Google uses information from sites that use its services
              </External>.
            </p>
          </section>

          <section>
            <h2>4. Information stored in your browser</h2>
            <p>Apart from advertising cookies, the website saves a little information in your browser so it works properly:</p>
            <ul>
              <li>your sign-in session, so you stay logged in;</li>
              <li>your cart, if you shop without signing in;</li>
              <li>the phone number from your last order, to fill in checkout faster;</li>
              <li>temporary payment details during a Khalti or Fonepay payment, cleared when the payment finishes.</li>
            </ul>
            <p>
              This information stays on your device and is not used to track you. Signing out removes your
              session, and clearing your browser&rsquo;s site data removes the rest.
            </p>
          </section>

          <section>
            <h2>5. Who we share information with</h2>
            <ul>
              <li><strong>Payment providers</strong> — Khalti and Fonepay, to process and confirm your payments.</li>
              <li><strong>Delivery</strong> — the staff or courier delivering your order receive your name, address and phone number.</li>
              <li><strong>Service providers</strong> — companies that host our website and store its data, only as needed to run it.</li>
              <li><strong>Google</strong> — for advertising (see section 3) and for the fonts used on this website.</li>
              <li><strong>Legal reasons</strong> — when the law of Nepal requires it, or to protect our customers, our business or the public from fraud or harm.</li>
            </ul>
          </section>

          <section>
            <h2>6. How long we keep it</h2>
            <p>
              We keep your account information while your account is open. Order and payment records are kept
              for as long as we need them for delivery, warranty, accounting and legal reasons.
            </p>
          </section>

          <section>
            <h2>7. Keeping your information safe</h2>
            <p>
              Our website uses HTTPS encryption, passwords are hashed, and only authorised staff can see customer
              and order information. No way of sending or storing data online is completely secure, but we work
              hard to protect yours.
            </p>
          </section>

          <section>
            <h2>8. Your choices</h2>
            <ul>
              <li>
                View and update your profile and password from your
                {' '}<Link to="/dashboard?tab=profile">account dashboard</Link>.
              </li>
              <li>
                Ask us for a copy of your information, to correct it, or to delete your account by contacting us
                below. We may still need to keep order records the law requires us to hold.
              </li>
              <li>Opt out of personalised ads in Google Ads Settings.</li>
              <li>Clear cookies and site data in your browser settings at any time.</li>
            </ul>
          </section>

          <section>
            <h2>9. Children</h2>
            <p>
              Our store is not meant for children under 16, and we do not knowingly collect their personal
              information. If you believe a child has given us their details, contact us and we will delete them.
            </p>
          </section>

          <section>
            <h2>10. Changes to this policy</h2>
            <p>
              We may update this policy when our store or the law changes. The date at the top of this page
              shows when it was last updated.
            </p>
          </section>

          <section>
            <h2>11. Contact us</h2>
            <p>For any privacy question or request, contact:</p>
            <address className="policy-contact">
              <strong>DJI Store Nepal</strong>
              <span>Ground Floor, Infinity Tower, Gongabu, Kathmandu, Nepal</span>
              <span>Email: <a href="mailto:info@djistorenepal.com">info@djistorenepal.com</a></span>
              <span>Phone: <a href="tel:+9779705743261">+977 9705743261</a></span>
            </address>
          </section>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
